import * as Dialog from '@radix-ui/react-dialog'
import { X } from 'lucide-react'

interface ModalShellProps {
  title: string
  subtitle?: string
  onClose: () => void
  children: React.ReactNode
}

/**
 * Accessible modal wrapper built on Radix Dialog.
 * Provides: focus trap, Escape-to-close, aria-modal, aria-labelledby,
 * focus restoration to trigger on close, and backdrop-click dismissal.
 */
const ModalShell = ({
  title,
  subtitle,
  onClose,
  children,
}: ModalShellProps) => (
  <Dialog.Root
    open
    onOpenChange={(open) => {
      if (!open) onClose()
    }}
  >
    <Dialog.Portal>
      <Dialog.Overlay className='fixed inset-0 z-50 bg-black/60 backdrop-blur-sm' />
      <Dialog.Content
        className='border-border-main bg-neutral fixed top-1/2 left-1/2 z-50 mx-4 w-full max-w-2xl -translate-x-1/2 -translate-y-1/2 rounded-2xl border shadow-2xl focus:outline-none'
        aria-describedby={undefined}
      >
        <div className='border-border-main flex items-center justify-between border-b px-6 py-4'>
          <div>
            <Dialog.Title className='text-text-primary font-semibold'>
              {title}
            </Dialog.Title>
            {subtitle && (
              <p className='mt-0.5 font-mono text-xs text-violet-400'>
                {subtitle}
              </p>
            )}
          </div>
          <Dialog.Close asChild>
            <button
              type='button'
              aria-label='Close'
              className='text-text-tertiary hover:bg-neutral-dimmed hover:text-text-primary rounded-md p-1.5'
            >
              <X className='h-4 w-4' />
            </button>
          </Dialog.Close>
        </div>

        {children}
      </Dialog.Content>
    </Dialog.Portal>
  </Dialog.Root>
)

export default ModalShell
